/**
 * OAuth helper utilities.
 *
 * This module centralizes construction of provider start URLs to ensure we set
 * required parameters correctly and consistently across the app.
 */

/**
 * Build a provider OAuth start URL from a base endpoint and a return path.
 *
 * - Always ensures an absolute `redirect_uri` query parameter is present using
 *   the current origin and the provided `returnPath` (e.g., "/login").
 * - Preserves existing query parameters on the base URL and will NOT override
 *   an existing non-empty `redirect_uri` value.
 * - If the base URL string is not a valid URL, returns it unchanged as a
 *   best-effort fallback (the caller may still navigate to it if it's valid).
 *
 * Examples:
 *   buildDiscordOAuthUrl('https://api.example.com/auth/discord', '/login')
 *   => 'https://api.example.com/auth/discord?redirect_uri=https%3A%2F%2Flocalhost%3A3000%2Flogin'
 *
 *   buildDiscordOAuthUrl('https://api/auth/discord?prompt=consent', '/signup')
 *   => 'https://api/auth/discord?prompt=consent&redirect_uri=https%3A%2F%2Flocalhost%3A3000%2Fsignup'
 */
export function buildDiscordOAuthUrl(baseUrl: string, returnPath: string): string {
  if (!baseUrl) return baseUrl;

  try {
    const url = new URL(baseUrl);

    const hasRedirectParam = url.searchParams.has('redirect_uri');
    const existing = url.searchParams.get('redirect_uri');

    // Only set redirect_uri if it's missing or empty
    if (!hasRedirectParam || (existing !== null && existing.trim() === '')) {
      const origin = window.location.origin.replace(/\/$/, '');
      const path = returnPath.startsWith('/') ? returnPath : `/${returnPath}`;
      const redirect = `${origin}${path}`;
      url.searchParams.set('redirect_uri', redirect);
    }

    return url.toString();
  } catch {
    // If URL parsing fails, return input unchanged to avoid breaking flows
    return baseUrl;
  }
}
